import { TASK_CLIENT_MESSAGE_MAX_CHARS, type TaskClientConversation } from '@fos/shared/contracts/work';
import { TASK_CLIENT_CONVERSATION_LABEL } from '@fos/shared/labels';
import { Mail, MessageSquareText } from 'lucide-react';
import { firstName, plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Section } from '../../../ui/Section';
import { ErrorState, ListSkeleton } from '../../../ui/States';
import { type ConversationItem, ConversationList, MessageComposer } from '../../portal';

/**
 * „Rozmowa z klientem” na karcie zadania (DECISIONS 2026-10-17 [P] „Model portalu klienta” nr 5): wiadomości
 * widoczne dla klienta w portalu, przy zgłoszeniu z portalu (`origin = 'client_portal'`). Komentarze wewnętrzne
 * zostają w historii (klient ich nie widzi). Odpowiedź pisze tylko człowiek w aplikacji (`tasks.clientMessages.add`
 * ma `denyVia: ['ai', 'mcp']`); osoby klienta dostają mail z linkiem do zgłoszenia (bez treści odpowiedzi).
 * Zespół po prawej (nasza strona), klient po lewej — jak w komunikatorze.
 */
export function ClientConversation({ taskId }: { taskId: string }) {
  const utils = trpc.useUtils();
  const conversation = trpc.tasks.clientMessages.list.useQuery({ taskId });
  const add = trpc.tasks.clientMessages.add.useMutation({
    onSuccess: (next) => {
      utils.tasks.clientMessages.list.setData({ taskId }, next);
      // ostatnia zmiana i historia karty
      void utils.tasks.get.invalidate({ id: taskId });
    },
  });

  return (
    <Section
      className="mt-4"
      aria-labelledby="rozmowa-z-klientem"
      title={
        <span id="rozmowa-z-klientem" className="inline-flex items-center gap-1.5">
          <MessageSquareText size={16} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
          {TASK_CLIENT_CONVERSATION_LABEL}
        </span>
      }
      tone="strong"
    >
      {conversation.isPending ? (
        <ListSkeleton rows={2} />
      ) : conversation.isError ? (
        <ErrorState compact onRetry={() => void conversation.refetch()} retrying={conversation.isFetching} />
      ) : (
        <ConversationBody
          data={conversation.data}
          sending={add.isPending}
          onSend={(text) => add.mutateAsync({ taskId, text })}
        />
      )}
    </Section>
  );
}

function ConversationBody({
  data,
  sending,
  onSend,
}: {
  data: TaskClientConversation;
  sending: boolean;
  onSend: (text: string) => Promise<unknown>;
}) {
  const items: ConversationItem[] = data.messages.map((m) => ({
    id: m.id,
    side: m.author === 'team' ? 'own' : 'other',
    authorName: m.person ? (m.author === 'team' ? firstName(m.person.name) : m.person.name) : null,
    authorNote: m.author === 'client' ? 'klient' : undefined,
    text: m.text,
    createdAt: m.createdAt,
  }));
  const people = data.clientPeople;
  const mailHint =
    people.length === 0
      ? 'Klient zobaczy odpowiedź w portalu. Nikt z klienta nie ma teraz aktywnego dostępu, więc mail nie wyjdzie.'
      : `Klient dostanie mail z linkiem do zgłoszenia (${people.map((p) => p.name).join(', ')}). Treść zobaczy w portalu.`;

  return (
    <>
      <p className="mb-4 text-caption text-fg-muted">
        Widzi ją klient w portalu zgłoszeń. Komentarze wewnętrzne (Historia) zostają tylko dla zespołu.
      </p>
      {items.length > 0 ? (
        <ConversationList items={items} className="mb-5" />
      ) : (
        <p className="mb-4 text-body-s text-fg-muted">Jeszcze nikt nie napisał.</p>
      )}
      {data.canWrite ? (
        <MessageComposer
          label="Odpowiedź do klienta"
          placeholder="Napisz odpowiedź do klienta…"
          maxLength={TASK_CLIENT_MESSAGE_MAX_CHARS}
          sending={sending}
          sendLabel="Wyślij do klienta"
          hint={
            <span className="inline-flex items-start gap-1.5">
              <Mail size={12} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
              {mailHint}
            </span>
          }
          onSend={onSend}
        />
      ) : (
        <p className="text-caption text-fg-muted">
          {data.messages.length > 0
            ? `${data.messages.length} ${plural(data.messages.length, ['wiadomość', 'wiadomości', 'wiadomości'])}. Odpowiedź wymaga edycji zadania (zadanie nie może być w archiwum).`
            : 'Odpowiedź wymaga edycji zadania (zadanie nie może być w archiwum).'}
        </p>
      )}
    </>
  );
}
